import type { UserProfile } from "@/lib/api";

/**
 * What the signed-in user may do, asked once and answered the same way
 * everywhere.
 *
 * The API is the authority — every write route enforces this server-side, and
 * a screen that got it wrong would only produce a 403. What this file is for
 * is not showing somebody a form they are not allowed to submit: an auditor
 * filling in a leaver's name and a date, only to be refused at the end, is a
 * worse answer than never offering the form.
 *
 * `mayRaise` comes from the API rather than being re-derived from the role
 * list, so the button and the route cannot drift apart. `mayActOn` has to be
 * computed here because it depends on the record, and the record is only on
 * this side of the call.
 */

export function mayRaise(me: UserProfile | undefined | null): boolean {
  return me?.may_raise === true;
}

/**
 * May see the operations view — the only thing the two roles disagree on.
 *
 * Admin is NOT a bigger version of the product: every functional capability
 * belongs to HR, because that is the job. What Admin adds is "is this working",
 * which is an IT question and clutter on an HR screen.
 */
export function maySeeOperations(me: UserProfile | undefined | null): boolean {
  return me?.may_see_operations === true;
}

/** The sentence shown where a control would have been. */
export const READ_ONLY_NOTE =
  "Your account is signed in but holds no HROps role, so you can read but not change anything. If you had one until recently, signing out and back in will pick it up.";
